import { throttle } from "lodash";
import { useEffect } from "react";

type EventHandlers = {
  enter: EventListenerOrEventListenerObject;
  leave: EventListenerOrEventListenerObject;
};

const eventListenersMap = new WeakMap<HTMLElement, EventHandlers>();
const registeredParents = new Set<HTMLElement>();

const adjustExplainPosition = (element: HTMLElement, parent: HTMLElement): void => {
  const calculatePosition = () => {
    const parentRect = parent.getBoundingClientRect();
    const elementHeight = element.getBoundingClientRect().height;

    element.style.position = "fixed";
    element.style.left = `${parentRect.left + parentRect.width / 2}px`;
    element.style.transform = "translateX(-50%)";
    element.style.top = "auto";
    element.style.bottom = `${window.innerHeight - parentRect.top - parentRect.height - elementHeight - 6}px`;
    element.style.maxWidth = `${parentRect.width}px`;
    element.style.opacity = "1";
    element.style.visibility = "visible";
  };

  calculatePosition();

  const resizeObserver = new ResizeObserver(() => {
    calculatePosition();
  });

  resizeObserver.observe(element);

  setTimeout(() => {
    resizeObserver.disconnect();
  }, 500);
};

const hideExplainPosition = (element: HTMLElement): void => {
  requestAnimationFrame(() => {
    element.style.opacity = "0";
    element.style.visibility = "hidden";
  });
};

const addHoverListener = (parent: HTMLElement): void => {
  const handleMouseEnter = throttle(() => {
    if (parent.querySelector("[class*='-status-error']")) {
      const explainElements = parent.querySelectorAll<HTMLElement>(".ant-form-item-explain");
      explainElements.forEach((element) => adjustExplainPosition(element, parent));
    }
  }, 100);

  const handleMouseLeave = throttle(() => {
    const explainElements = parent.querySelectorAll<HTMLElement>(".ant-form-item-explain");
    explainElements.forEach((element) => hideExplainPosition(element));
  }, 100);

  parent.addEventListener("mouseenter", handleMouseEnter);
  parent.addEventListener("mouseleave", handleMouseLeave);

  eventListenersMap.set(parent, { enter: handleMouseEnter, leave: handleMouseLeave });
  registeredParents.add(parent);
};

const FormItemExplainHandler: React.FC = () => {
  useEffect(() => {
    const initializeExplainElements = (): void => {
      const itemControls = document.querySelectorAll<HTMLElement>(".ant-form-item-control");
      itemControls.forEach((parent) => {
        if (!registeredParents.has(parent)) {
          addHoverListener(parent);
        }
      });
    };

    initializeExplainElements();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type !== "childList") {
          return;
        }
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            const itemControls = node.closest<HTMLElement>(".ant-form-item-control");
            if (itemControls) {
              if (
                itemControls.matches(":hover") &&
                (itemControls.querySelector("[class*='-status-error']") ||
                  itemControls.querySelector("[class*='-status-warning']"))
              ) {
                const explainElements = itemControls.querySelectorAll<HTMLElement>(".ant-form-item-explain");
                explainElements.forEach((element) => adjustExplainPosition(element, itemControls));
              }
            }
            if (node.matches(".ant-form-item-control") || node.querySelector(".ant-form-item-control")) {
              initializeExplainElements();
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const handleVisibility = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          const element = entry.target as HTMLElement;
          hideExplainPosition(element);
        }
      });
    };

    const intersectionObserver = new IntersectionObserver(handleVisibility, { threshold: 0 });

    registeredParents.forEach((parent) => {
      const explainElements = parent.querySelectorAll<HTMLElement>(".ant-form-item-explain");
      explainElements.forEach((element) => {
        intersectionObserver.observe(element);
      });
    });

    return () => {
      observer.disconnect();
      intersectionObserver.disconnect();
      registeredParents.forEach((parent) => {
        const handlers = eventListenersMap.get(parent);
        if (handlers) {
          parent.removeEventListener("mouseenter", handlers.enter);
          parent.removeEventListener("mouseleave", handlers.leave);
        }
      });
      registeredParents.clear();
    };
  }, []);

  return null;
};

export default FormItemExplainHandler;
